import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import { BalanceOperationTab, BalanceOperationTabAction, TransactionType } from "../../../../enums";
import { REGEX_NUMERIC, RolesDropdownData, T } from "../../../../constants";
import { dataToDropDownConverter, formatNumber } from "../../../../helpers";
import { useStore, useTranslation } from "../../../../hooks";
import { Dropdown, Input, Modal } from "../../shared";
import { useForm } from "../../../../hooks/use-form";
import SuccessPopUp from "./success-popup";

interface EditBalanceLimitModalProps {}

const initialValues = {
  type: "",
  amount: "",
  comment: "",
  pinCode: "",
  userType: "",
  assigneeId: "",
};

export const EditBalanceLimitModal: React.FC<EditBalanceLimitModalProps> = observer(() => {
    const {
      userStore,
      managerStore,
      modalStore: { setIsOpenBalanceAndLimitModal },
    } = useStore();

    const {
      wallet: { data: { balance, currencyCode } },
      userList: { data: { items: userList } }
    } = userStore;

    useEffect(() => {
      return () => {
        userStore.resetUserList();
      }
    }, []);

    const {
      isShowSuccessesPart,
      errorMessageForManagerBalance,
      setErrorMessageForManagerBalance,
      setIsShowSuccessesPart
    } = managerStore;

    const [activeTab, setActiveTab] = useState(BalanceOperationTab.TransFer);
    const [activeTabAction, setActiveTabAction] = useState(BalanceOperationTabAction.SendMoneyToOrCashIn);

    const { values, setValues, handleDropdownChange, handlePositiveNumberChange } = useForm(initialValues);

    const t = useTranslation();

    const handleClose = useCallback(() => {
      setIsOpenBalanceAndLimitModal(false);
      setIsShowSuccessesPart(false);
      setErrorMessageForManagerBalance("");
    }, []);

    const handleBalanceTransaction = useCallback(() => {
      if (activeTab === BalanceOperationTab.Encashment && activeTabAction === BalanceOperationTabAction.SendMoneyToOrCashIn) {
        managerStore.cashInBalance({
          amount: values.amount,
          comment: values.comment,
          pinCode: +values.pinCode,
        });
        return;
      }
      if (activeTab === BalanceOperationTab.Encashment && activeTabAction === BalanceOperationTabAction.GetMoneyFromOrCashOut) {
        managerStore.cashOutBalance({
          amount: values.amount,
          comment: values.comment,
          pinCode: +values.pinCode,
        });
        return;
      }
      if (activeTab === BalanceOperationTab.TransFer) {
        managerStore.balanceTransaction({
          ...values,
          type: activeTabAction === BalanceOperationTabAction.SendMoneyToOrCashIn ? TransactionType.Send : TransactionType.Receive,
          pinCode: +values.pinCode,
          amount: +values.amount,
        });
      }
    }, [values, activeTabAction, activeTab]);

    const resetErrorMessage = useCallback((value: string) => {
      if (!value) {
        setErrorMessageForManagerBalance("");
      }
    }, []);

    const handlePinCode = useCallback((value: string) => {
      resetErrorMessage(value);
      if (REGEX_NUMERIC.test(value) && value.length <= 5) {
        setValues((prevValues) => ({...prevValues, pinCode: value}));
      }
    }, []);

    const handleChangeAmount = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const {value} = e.target;
      resetErrorMessage(value);
      handlePositiveNumberChange(e, "amount");
    }, [handlePositiveNumberChange]);

    const handleReceiverRoleDropdownChange = useCallback((value: number) => {
      setValues((prevValues) => ({ ...prevValues, userType: value }));
      userStore.getUserList({ userType: value });
    }, []);

    const receiversDropdownData = useMemo(() => {
      return dataToDropDownConverter(userList, {labelKey: "userName", valueKey: "id"});
    }, [userList]);

    const fieldsConfig = useMemo(() => {
      const isSendMoneyTo = activeTabAction === BalanceOperationTabAction.SendMoneyToOrCashIn;
      return {
        rolDropDownLabel: isSendMoneyTo ? T.RECEIVER_ROLE : T.SENDER_ROLE,
        receiverOrSenderDropdownLabel: isSendMoneyTo ? T.RECEIVER : T.SENDER,
      }
    }, [activeTabAction]);

    const actionButtonName = useMemo(() => {
      return activeTab === BalanceOperationTab.TransFer || (activeTab === BalanceOperationTab.Encashment && activeTabAction === BalanceOperationTabAction.SendMoneyToOrCashIn) ?
        t(T.DEPOSIT) : t(T.WITHDRAW);
    }, [activeTab, activeTabAction]);

    const handleChangeActiveTab = useCallback((tab: BalanceOperationTab) => {
      setActiveTab(tab);
      userStore.resetUserList();
      setValues(initialValues);
      setActiveTabAction(BalanceOperationTabAction.SendMoneyToOrCashIn);
    }, []);

    const handleChangeActiveTabAction = useCallback((actionType: BalanceOperationTabAction) => {
      setActiveTabAction(actionType);
      userStore.resetUserList();
      setValues(initialValues);
    }, []);

    const disabledActionButton = useMemo(() => {
      const { amount, pinCode, assigneeId } = values;
      return activeTab === BalanceOperationTab.TransFer ? !amount || !pinCode || !assigneeId : !amount || !pinCode;
    }, [values]);

    const handleCommentChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = event.target;
      if (value.length > 100) return;
      setValues((prevValues) => ({ ...prevValues, comment: value }));
    }, []);

    return (
      <Modal onClose={handleClose} title={T.BALANCE_OPERATIONS}>
        {/*TODO: It is necessary to divide into components*/}
        {!isShowSuccessesPart ? (
            <div className="balanceOperationsWrapper">
              <div className="balanceOperationsPopupContent">
                <div className="balanceOperationsCnt">
                  <div className="popupTabsHolder">
                    <div
                      onClick={() => handleChangeActiveTab(BalanceOperationTab.TransFer)}
                      className={`popupTab truncate ${activeTab === BalanceOperationTab.TransFer ? "active" : ""}`}
                    >
                      {t(T.TRANSFER)}
                    </div>
                    <div
                      onClick={() => handleChangeActiveTab(BalanceOperationTab.Encashment)}
                      className={`popupTab truncate ${activeTab === BalanceOperationTab.Encashment ? "active" : ""} `}
                    >
                      {t(T.ENCASHMENT)}
                    </div>
                  </div>
                    <div className="moneyTransferContainer">
                        <div className="moneyTransferItemContainer">
                            <div
                              onClick={() => handleChangeActiveTabAction(BalanceOperationTabAction.SendMoneyToOrCashIn)}
                              className={`moneyTransferItem ${activeTabAction === BalanceOperationTabAction.SendMoneyToOrCashIn ? "active" : ""}`}
                            >
                              <span className="moneyTransferItemTxt">
                                {activeTab === BalanceOperationTab.TransFer ? t(T.SEND_MONEY_TO) : t(T.CASH_IN)}
                              </span>
                            </div>
                            <div
                              onClick={() => handleChangeActiveTabAction(BalanceOperationTabAction.GetMoneyFromOrCashOut)}
                              className={`moneyTransferItem ${activeTabAction === BalanceOperationTabAction.GetMoneyFromOrCashOut ? "active" : ""}`}
                            >
                              <span className="moneyTransferItemTxt">
                                {activeTab === BalanceOperationTab.TransFer ? t(T.GET_MONEY_FROM) : t(T.CASH_OUT)}
                              </span>
                            </div>
                        </div>
                    </div>
                  <div className="popupBalanceInfo">
                    <div className="popupBalanceInfoRow">
                      <span className="popupBalanceInfoText">
                        {t(T.CURRENT)} {t(T.BALANCE)}:
                      </span>
                      <span className="popupBalanceInfoText">
                        {formatNumber(balance)} {currencyCode}
                      </span>
                    </div>
                  </div>
                  <div className="popupInputsContainer">
                    <div className="popupInputRow">
                      <div className="popupInputHolder">
                        {
                         activeTab === BalanceOperationTab.TransFer && (
                           <>
                             <Dropdown
                               direction="move-down"
                               value={values.userType}
                               data={RolesDropdownData}
                               placeholder={fieldsConfig.rolDropDownLabel}
                               onChange={(value) => handleReceiverRoleDropdownChange(value as number)}
                             />
                             <Dropdown
                               direction="move-down"
                               value={values.assigneeId}
                               data={receiversDropdownData}
                               placeholder={fieldsConfig.receiverOrSenderDropdownLabel}
                               onChange={(value) => handleDropdownChange(value as number, "assigneeId")}
                             />
                           </>
                          )
                        }
                        <Input
                          type="number"
                          autoComplete="off"
                          value={values.amount}
                          className="popupInput"
                          placeholder={t(T.AMOUNT)}
                          onChange={(e) => handleChangeAmount(e)}
                        />
                        <Input
                          type="password"
                          autoComplete="off"
                          className="popupInput"
                          placeholder={t(T.PIN)}
                          value={values.pinCode}
                          onChange={(e) => handlePinCode(e.target.value)}
                        />
                        {
                          activeTab === BalanceOperationTab.Encashment && (
                            <textarea
                                value={values.comment}
                                placeholder={t(T.COMMENT)}
                                onChange={(value) => handleCommentChange(value)}
                            />
                          )
                        }
                      </div>
                    </div>
                    {(values.pinCode || values.amount) && errorMessageForManagerBalance && (
                      <p className="inputMessage error">
                        {t(errorMessageForManagerBalance)}
                      </p>
                    )}
                  </div>
                  <div className="btn-padding-holder">
                    <button
                      onClick={handleBalanceTransaction}
                      className={`common-button pp-button active full ${disabledActionButton ? "disabled" : ""}`}
                    >
                      {actionButtonName}
                    </button>
                  </div>
                </div>
              </div>
            </div>
        ) : (
          <SuccessPopUp />
        )}
      </Modal>
    );
  });
